import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, update } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import { useLanguage } from '../LanguageContext';

export default function MyBookingsPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('passenger');
  const [expandedCard, setExpandedCard] = useState(null);

  const [myRequests, setMyRequests] = useState([]);
  const [myRides, setMyRides] = useState([]);
  const [requestsForMyRides, setRequestsForMyRides] = useState([]);

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  useEffect(() => {
    if (!user || !user.phone) return;

    const requestsRef = ref(db, 'bookingRequests/');
    const unsubReq = onValue(requestsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const all = Object.keys(data).map(key => ({ id: key, ...data[key] }));
        setMyRequests(all.filter(r => r.passengerPhone === user.phone).reverse());
        setRequestsForMyRides(all.filter(r => r.driver === user.name));
      } else {
        setMyRequests([]);
        setRequestsForMyRides([]);
      }
    });

    const ridesRef = ref(db, 'rides/');
    const unsubRides = onValue(ridesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data)
          .map(key => ({ id: key, ...data[key] }))
          .filter(r => r.driver === user.name && r.status !== 'cancelled_by_driver');
        setMyRides(list.reverse());
      } else {
        setMyRides([]);
      }
    });

    return () => { unsubReq(); unsubRides(); };
  }, [user]);

  const toggleExpand = (id) => setExpandedCard(expandedCard === id ? null : id);

  const statusLabel = (status) => {
    if (status === 'pending_approval') return { text: '⏳ Awaiting driver approval', color: '#8a6416', bg: '#fdf3e3' };
    if (status === 'approved') return { text: '✅ Approved', color: '#166534', bg: '#eaf4ea' };
    if (status === 'rejected') return { text: '❌ Declined', color: '#e94560', bg: '#fdecec' };
    return { text: status, color: '#5a5f7d', bg: '#f7f9fc' };
  };

  const respondToRequest = async (requestId, approve, requestSeats, rideId) => {
    try {
      if (approve) {
        const ride = myRides.find(r => r.id === rideId);
        if (ride) {
          const alreadyApproved = requestsForMyRides
            .filter(r => r.rideId === rideId && r.status === 'approved')
            .reduce((sum, r) => sum + (r.seats || 0), 0);
          if (alreadyApproved + (requestSeats || 0) > ride.seats) {
            alert('Not enough seats remaining on this ride to approve this request.');
            return;
          }
        }
      }
      await update(ref(db, 'bookingRequests/' + requestId), {
        status: approve ? 'approved' : 'rejected',
        respondedAt: new Date().toISOString(),
      });
      alert(approve ? '✅ Approved! Passenger can now pay.' : '❌ Request declined.');
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  const cancelRideAsDriver = async (rideId, route) => {
    if (!window.confirm('Cancel your ride ' + route + '? All pending requests will be declined.')) return;
    try {
      const relatedPending = requestsForMyRides.filter(r => r.rideId === rideId && r.status === 'pending_approval');
      for (const req of relatedPending) {
        await update(ref(db, 'bookingRequests/' + req.id), {
          status: 'rejected', respondedAt: new Date().toISOString(), cancelReason: 'Driver cancelled the ride',
        });
      }
      await update(ref(db, 'rides/' + rideId), { status: 'cancelled_by_driver', cancelledAt: new Date().toISOString() });

      const cancelledRide = myRides.find(r => r.id === rideId);
      const linkedId = cancelledRide?.linkedRideId;
      if (linkedId) {
        const linkedPending = requestsForMyRides.filter(r => r.rideId === linkedId && r.status === 'pending_approval');
        for (const req of linkedPending) {
          await update(ref(db, 'bookingRequests/' + req.id), {
            status: 'rejected', respondedAt: new Date().toISOString(), cancelReason: 'Driver cancelled the linked ride',
          });
        }
        await update(ref(db, 'rides/' + linkedId), { status: 'cancelled_by_driver', cancelledAt: new Date().toISOString() });
      }

      alert(linkedId ? 'Both your outbound and return rides have been cancelled.' : 'Ride cancelled.');
    } catch (error) {
      alert('Error: ' + error.message);
    }
  };

  if (!user) return null;

  const incomingPending = requestsForMyRides.filter(r => r.status === 'pending_approval');
  const incomingHandled = requestsForMyRides.filter(r => r.status !== 'pending_approval' && r.status !== 'cancelled_by_passenger');

  const rideSeatSummary = myRides.map(ride => {
    const approvedForRide = requestsForMyRides.filter(r => r.rideId === ride.id && r.status === 'approved');
    const seatsBooked = approvedForRide.reduce((sum, r) => sum + (r.seats || 0), 0);
    return { ...ride, approvedPassengers: approvedForRide, seatsBooked, seatsRemaining: Math.max(0, ride.seats - seatsBooked) };
  });

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />
      <div style={styles.container}>

        <h2 style={styles.pageTitle}>🎫 {t('myBookings')}</h2>

        <div style={styles.tabRow}>
          <button style={activeTab === 'passenger' ? styles.tabBtnActive : styles.tabBtn} onClick={() => setActiveTab('passenger')}>As Passenger</button>
          <button style={activeTab === 'driver' ? styles.tabBtnActive : styles.tabBtn} onClick={() => setActiveTab('driver')}>
            As Driver{incomingPending.length > 0 ? ' (' + incomingPending.length + ')' : ''}
          </button>
        </div>

        {activeTab === 'passenger' && (
          myRequests.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🎫</div>
              <h3 style={styles.emptyTitle}>No bookings yet</h3>
              <p style={styles.emptyText}>Book a ride from Home to see it here</p>
            </div>
          ) : (
            <div style={styles.grid}>
              {myRequests.map(r => {
                const status = statusLabel(r.status);
                return (
                  <div key={r.id} style={styles.card}>
                    <span style={{ ...styles.statusBadge, color: status.color, background: status.bg }}>{status.text}</span>
                    <div style={styles.route}>{r.from} → {r.to}</div>
                    <div style={styles.meta}>📅 {r.date} · {r.seats} seat{r.seats > 1 ? 's' : ''} · ₹{r.totalAmount} · {r.driver}</div>
                    {r.status === 'approved' && (
                      <button
                        style={styles.payNowBtn}
                        onClick={() => navigate(
                          '/payment?requestId=' + r.id +
                          '&from=' + encodeURIComponent(r.from) + '&to=' + encodeURIComponent(r.to) +
                          '&date=' + encodeURIComponent(r.date) + '&driver=' + encodeURIComponent(r.driver) +
                          '&driverPhone=' + encodeURIComponent(r.driverPhone || '') +
                          '&seats=' + r.seats + '&pricePerSeat=' + r.pricePerSeat
                        )}
                      >💳 Pay Now · ₹{r.totalAmount}</button>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}

        {activeTab === 'driver' && (
          <>
            {incomingPending.length > 0 && (
              <>
                <h3 style={styles.sectionTitle}>New Requests ({incomingPending.length})</h3>
                <div style={styles.grid}>
                  {incomingPending.map(r => (
                    <div key={r.id} style={styles.requestCard}>
                      <div style={styles.route}>{r.from} → {r.to}</div>
                      <div style={styles.meta}>👤 {r.passengerName} · 📅 {r.date} · {r.seats} seat{r.seats > 1 ? 's' : ''} · ₹{r.totalAmount}</div>
                      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                        <button style={styles.rejectBtn} onClick={() => respondToRequest(r.id, false, r.seats, r.rideId)}>Reject</button>
                        <button style={styles.approveBtn} onClick={() => respondToRequest(r.id, true, r.seats, r.rideId)}>Approve</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <h3 style={styles.sectionTitle}>Your Posted Rides</h3>
            {rideSeatSummary.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>🚗</div>
                <h3 style={styles.emptyTitle}>No rides posted yet</h3>
                <p style={styles.emptyText}>Post a ride to see it here</p>
                <button style={styles.emptyBtn} onClick={() => navigate('/offer')}>Post a Ride</button>
              </div>
            ) : (
              <div style={styles.grid}>
                {rideSeatSummary.map(ride => (
                  <div key={ride.id} style={styles.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ ...styles.statusBadge, color: '#166534', background: '#eaf4ea' }}>Active</span>
                      <button style={styles.cancelChip} onClick={() => cancelRideAsDriver(ride.id, ride.from + ' → ' + ride.to)}>Cancel Ride</button>
                    </div>
                    <div style={styles.route}>{ride.from} → {ride.to}</div>
                    <div style={styles.meta}>📅 {ride.date} · ⏰ {ride.time} → {ride.reachTime || '—'} · {ride.seats} seats · ₹{ride.price}/seat</div>
                    {ride.hasReturnJourney && (
                      <div style={styles.returnNote}>🔁 Return: {ride.returnDate} · {ride.returnDepartTime}</div>
                    )}
                    <div style={styles.seatRow}>
                      <div style={styles.seatBox}><b>{ride.seats}</b><span>Total</span></div>
                      <div style={styles.seatBox}><b style={{ color: '#00c896' }}>{ride.seatsBooked}</b><span>Booked</span></div>
                      <div style={styles.seatBox}><b style={{ color: '#f5a623' }}>{ride.seatsRemaining}</b><span>Left</span></div>
                    </div>
                    <button style={styles.expandLink} onClick={() => toggleExpand(ride.id)}>
                      {expandedCard === ride.id ? 'Hide passengers' : 'View passengers'}
                    </button>
                    {expandedCard === ride.id && (
                      <div style={styles.passengerList}>
                        {ride.approvedPassengers.length === 0 ? (
                          <p style={{ fontSize: 12, color: '#9499b5', textAlign: 'center' }}>No confirmed passengers yet</p>
                        ) : (
                          ride.approvedPassengers.map((p, i) => (
                            <div key={i} style={styles.passengerRow}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{p.passengerName}</div>
                                <div style={{ fontSize: 11, color: '#9499b5' }}>{p.passengerPhone}</div>
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>{p.seats} seat{p.seats > 1 ? 's' : ''}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {incomingHandled.length > 0 && (
              <>
                <h3 style={styles.sectionTitle}>Request History</h3>
                <div style={styles.grid}>
                  {incomingHandled.map(r => (
                    <div key={r.id} style={styles.card}>
                      <span style={{ ...styles.statusBadge, ...(r.status === 'approved' ? { color: '#166534', background: '#eaf4ea' } : { color: '#e94560', background: '#fdecec' }) }}>
                        {r.status === 'approved' ? 'Approved' : 'Rejected'}
                      </span>
                      <div style={styles.route}>{r.from} → {r.to}</div>
                      <div style={styles.meta}>👤 {r.passengerName} · 📅 {r.date}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  container: { maxWidth: 900, margin: '0 auto', padding: '30px 20px 40px' },
  pageTitle: { color: '#1a1a2e', marginBottom: 16 },
  tabRow: { display: 'flex', gap: 10, marginBottom: 24 },
  tabBtn: { flex: 1, height: 42, borderRadius: 10, border: '1px solid #eef1f8', background: '#f7f9fc', color: '#5a5f7d', fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  tabBtnActive: { flex: 1, height: 42, borderRadius: 10, border: '1px solid #3a8fd0', background: '#3a8fd0', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 },
  sectionTitle: { color: '#1a1a2e', margin: '24px 0 12px' },
  emptyState: { background: '#f7f9fc', borderRadius: 14, padding: 40, textAlign: 'center' },
  emptyIcon: { fontSize: 44, marginBottom: 10 },
  emptyTitle: { color: '#1a1a2e', margin: '0 0 4px' },
  emptyText: { color: '#9499b5', margin: '0 0 16px' },
  emptyBtn: { background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, cursor: 'pointer' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 },
  card: { border: '1px solid #eef1f8', borderRadius: 14, padding: 16, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' },
  requestCard: { border: '1.5px solid #e94560', background: '#fff5f7', borderRadius: 14, padding: 16 },
  statusBadge: { display: 'inline-block', borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 700, marginBottom: 10 },
  route: { fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
  meta: { fontSize: 12, color: '#5a5f7d' },
  returnNote: { fontSize: 11, color: '#185fa5', fontWeight: 700, marginTop: 4 },
  payNowBtn: { width: '100%', height: 36, background: '#00c896', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12, marginTop: 8 },
  rejectBtn: { flex: 1, height: 38, borderRadius: 8, border: '1.5px solid #e94560', background: '#fff', color: '#e94560', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  approveBtn: { flex: 1, height: 38, borderRadius: 8, border: 'none', background: '#00c896', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 12 },
  cancelChip: { border: '1.5px solid #e94560', color: '#e94560', background: '#fff', borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  seatRow: { display: 'flex', gap: 8, margin: '10px 0' },
  seatBox: { flex: 1, background: '#f7f9fc', borderRadius: 8, padding: 8, textAlign: 'center', display: 'flex', flexDirection: 'column', fontSize: 10, color: '#9499b5' },
  expandLink: { background: 'none', border: 'none', color: '#e94560', fontWeight: 600, fontSize: 12, cursor: 'pointer', padding: 0 },
  passengerList: { marginTop: 10, background: '#f7f9fc', borderRadius: 10, padding: 10 },
  passengerRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eef1f8' },
};